import { inject } from 'vue';
import { CommercialApiConfig } from '@/services/CommercialApiConfig';
import { apiSend } from '@/utils/api-helpers';
import { useFetch } from '@/composables/UseFetch';
import { AuthService } from '@/contracts/AuthService';
import type {
  RsmSummaryRow,
  RsmUpsertRegionRequest,
  RsmUpsertAssignmentRequest,
  RsmUpsertResult,
  RsmDeleteResult,
} from '@/models/RsmViewModels';

// ── composable ─────────────────────────────────────────────────────────

export function useRsmApi() {
  const base = () => `${CommercialApiConfig.endpoint}/rsm`;
  const fetchUtils = useFetch(inject('auth') as AuthService);
  const authFetch = (url: string, options?: RequestInit) => fetchUtils.fetchResilientWithAuth(url, options);

  const send = <T>(method: string, url: string, body?: unknown) => apiSend<T>(method, url, body, authFetch);

  // ── public API ─────────────────────────────────────────────────────────

  function getSummary(environment = 'snd4'): Promise<RsmSummaryRow[]> {
    return send<RsmSummaryRow[]>('GET', `${base()}/summary?environment=${environment}`);
  }

  function upsertRegion(request: RsmUpsertRegionRequest): Promise<RsmUpsertResult> {
    return send<RsmUpsertResult>('PUT', `${base()}/region`, request);
  }

  function upsertAssignment(request: RsmUpsertAssignmentRequest): Promise<RsmUpsertResult> {
    return send<RsmUpsertResult>('PUT', `${base()}/assignment`, request);
  }

  function deleteRegion(regCode: string): Promise<RsmDeleteResult> {
    return send<RsmDeleteResult>('DELETE', `${base()}/region/${encodeURIComponent(regCode)}`);
  }

  function deleteAssignment(regCode: string): Promise<RsmDeleteResult> {
    return send<RsmDeleteResult>('DELETE', `${base()}/assignment/${encodeURIComponent(regCode)}`);
  }

  return {
    getSummary,
    upsertRegion,
    upsertAssignment,
    deleteRegion,
    deleteAssignment,
  };
}
